import React, { useEffect, useState } from 'react';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import remarkGfm from 'remark-gfm';
import { Meta } from '../../Meta';
import { LOGGED_IN_APP_BASE_URL } from '../../../utils/url';
import { MarketingPage } from '../../marketing/MarketingPage';
import { PostData } from '../utils';
import Section from '../../marketing/Section';
import Text from '@/components/marketing/Text';
import { CTABox } from '../../marketing/CTABox';
import BlogPostHeader from './BlogPostHeader';
import CustomerQuote from "@/components/customers/case-studies/CustomerQuote";
import DemoLink from '@/components/blog/DemoLink';

interface Props {
  postData: PostData;
}

export const BlogPost: React.FC<Props> = ({ postData }) => {
  const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult | null>(null);

  useEffect(() => {
    const compileMDX = async () => {
      const compiled = await serialize(postData.content, {
        mdxOptions: {
          remarkPlugins: [remarkGfm],
        },
      });
      setMdxSource(compiled);
    };
    compileMDX();
  }, [postData.content]);

  const components = {
    Section: (props) => <Section {...props} />,
    BlogPostHeader: (props) => <BlogPostHeader {...props} />,
    CustomerQuote: (props) => <CustomerQuote {...props} />,
    DemoLink: (props) => <DemoLink {...props} />,
    a: ({ href, children }: JSX.IntrinsicElements['a']) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="font-semibold underline text-silvermist-950 underline-offset-[3px] hover:text-silvermist-800 transition-colors"
      >
        {children}
      </a>
    ),
    h2: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none' className="mt-6 mb-2 [&:first-child>*]:mt-0">
          <Text as="h2" color="primary" size="xl" weight="semibold">
            {children}
          </Text>
      </Section>
    ),
    h3: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none' className="mt-6 mb-2">
          <Text as="h3" color="primary" size="lg" weight="semibold">
            {children}
          </Text>
      </Section>
    ),
    p: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <Text as="p" className="leading-8 mt-2 mb-2 [&:first-child>*]:mt-0 whitespace-pre-line">
          {children}
        </Text>
      </Section>
    ),
    ul: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <ul className="list-disc pl-6 mt-2 mb-2 space-y-2 leading-loose">
          {children}
        </ul>
      </Section>
    ),
    ol: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <ol className="list-decimal pl-6 mt-2 mb-2 space-y-2 leading-loose">
          {children}
        </ol>
      </Section>
    ),
    li: ({ children }) => (
      <Text as="li" className="leading-6 before:inline leading-loose">{children}</Text>
    ),
    img: ({ src, alt }) => (
      <img
        src={src}
        alt={alt}
        className="w-full h-auto object-cover rounded-lg my-4"
      />
    ),
    table: ({ children }) => (
      <Section contentWidth='content-xxs' padding='p-none'>
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-silvermist-200 text-silvermist-900 table-auto">
            {children}
          </table>
        </div>
      </Section>
    ),
    thead: ({ children }) => <thead className="bg-silvermist-100">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="even:bg-silvermist-50">{children}</tr>,
    th: ({ children }) => (
      <th className="p-3 border border-silvermist-200 text-silvermist-900 font-semibold">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="p-3 border border-silvermist-200 text-silvermist-900 whitespace-pre-wrap">
        {children}
      </td>
    ),
    br: () => <br />,
  };

  return (
    <MarketingPage>
      <Meta title={`${postData.title}`}>
        <meta name="description" content={postData.description} />
        <meta name="author" content={postData.authors.join(', ')} />
        <meta name="publish_date" property="og:publish_date" content={postData.date} />
        <meta property="og:title" content={`${postData.title}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${LOGGED_IN_APP_BASE_URL}/blog/${postData.slug}`} />
        <meta
          property="og:image"
          content={`${LOGGED_IN_APP_BASE_URL}/api/og?img=${encodeURIComponent(
            postData.featuredImage
          )}`}
        />
        <meta property="og:description" content={postData.description} />
      </Meta>

      <BlogPostHeader
        title={postData.title}
        description={postData.description}
        featuredImage={postData.featuredImage}
        date={postData.date}
        authors={postData.authors}
        readingTime={postData.readingTime}
      />

      <div className='w-full max-w-[1100px] mx-auto border-x border-silvermist-100'>
        <div className='w-full max-w-[1000px] mx-auto py-20 border-x border-dashed border-silvermist-100'>
          <div className='flex flex-col items-center text-silvermist-600'>
            {mdxSource && <MDXRemote {...mdxSource} components={components} />}
          </div>
        </div>
      </div>

      <Section contentWidth='content-sm' padding='p-none' className='border border-silvermist-100 mb-0 lg:mb-10'>
        <CTABox roundedBorder={false} border={false} />
      </Section>

    </MarketingPage>
  );
};